import BaseController from '../BaseController';
import { SuggestionList } from './SuggestionList';
import { Logger } from '_common/services';
import { IndexerDeltaType } from '../Models/ModelIndexer';
import { RichTextParser } from '../../common';
import { NodeUtils } from '../../models';

type SuggestionControllerArgType = Pick<Editor.Data.State, 'transport' | 'models' | 'context'>;

type Timeouts = {
  createSuggestionData?: NodeJS.Timeout;
  updateSuggestionData?: NodeJS.Timeout;
};

type SuggestionData = {
  type: Editor.Data.Suggestions.SuggestionDataType;
  insertedContent: Editor.Data.Suggestions.SuggestionContentData | undefined;
  deletedContent: Editor.Data.Suggestions.SuggestionContentData | undefined;
};

export class SuggestionController extends BaseController {
  protected name: string = 'SuggestionController';

  private suggestionList?: SuggestionList;

  private timeouts: Timeouts;

  private suggestionRefsToCreate: string[];
  private suggestionRefsToUpdate: string[];

  private rescheduleCreateSuggestionDataCounter: number = 0;

  constructor(Data: SuggestionControllerArgType) {
    super(Data);
    this.handleSuggestionListError = this.handleSuggestionListError.bind(this);
    this.handleSuggestionListChanged = this.handleSuggestionListChanged.bind(this);
    this.handleSuggestionChanged = this.handleSuggestionChanged.bind(this);
    this.handleSuggestionCreated = this.handleSuggestionCreated.bind(this);
    this.updateSuggestionData = this.updateSuggestionData.bind(this);

    this.suggestionRefsToUpdate = [];
    this.suggestionRefsToCreate = [];
    this.timeouts = {};

    this.DEBUG = true;
  }

  debugMessage(...args: Parameters<Common.Logger['debug']>) {
    if (this.DEBUG) {
      Logger.trace(...args);
    }
  }

  get list() {
    return this.suggestionList;
  }

  isSuggestionCreated(suggestionId: string) {
    const model = this.Data.models?.get('SUGGESTION', suggestionId);
    return model && model.loaded === true && model.selectedData() != null;
  }

  getSuggestionById(suggestionId: string) {
    return this.Data.models?.get('SUGGESTION', suggestionId);
  }

  start(documentId: string): void {
    this.suggestionList = this.Data.models?.getIndexer(
      this.Data.models.TYPE_NAME.SUGGESTION,
    ) as SuggestionList;
    this.suggestionList.on('ERROR', this.handleSuggestionListError);
    this.suggestionList?.on('CHANGED_DELTA', this.handleSuggestionListChanged);
    this.suggestionList.start(documentId);
  }

  private async handleSuggestionListChanged(data: IndexerDeltaType<string[]>) {
    this.debugMessage('[SuggestionController] handleSuggestionListChanged', data);

    const dataObjs = this.suggestionList?.data || {};

    const jobsList: Promise<void>[] = [];

    if (data.in.length > 0) {
      for (let index = 0; index < data.in.length; index++) {
        let job: Promise<void> | undefined = undefined;

        const element = dataObjs[data.in[index]];
        if (element) {
          if (element.loaded !== true) {
            // WARN:
            // needs to wait always for all suggestions data to be loaded
            // or created before sending the update to redux

            if (
              element.selectedData() == null &&
              this.suggestionRefsToCreate.includes(data.in[index])
            ) {
              job = element.awaitForEvent('CREATED');
              this.scheduleCreateSuggestionData(0);
            } else {
              job = element.awaitForEvent('LOADED');
            }
            if (job) {
              job
                .then(() => {
                  // element.on('LOADED', this.handleSuggestionChanged);
                  element.on('CREATED', this.handleSuggestionCreated); // WARN: needs to be here for remote created suggestions
                  element.on('UPDATED', this.handleSuggestionChanged);
                })
                .catch((error) => {
                  Logger.error(error);
                });

              jobsList.push(job);
            }
          } else {
            // element.on('LOADED', this.handleSuggestionChanged);
            // element.on('CREATED', this.handleSuggestionCreated);
            // element.on('UPDATED', this.handleSuggestionChanged);
          }
        } else {
          Logger.error(
            `[SuggestionController] handleSuggestionListChanged: no model found for suggestion ${data.in[index]}`,
          );
        }
      }
    }

    await Promise.all(jobsList);

    this.loadSuggestionData();
  }

  private loadSuggestionData() {
    let suggestionData: Editor.Data.Suggestions.SuggestionsMapper = {};
    const suggestionList = this.suggestionList?.list || [];
    const dataObjs = this.suggestionList?.data || {};
    for (let index = 0; index < suggestionList.length; index++) {
      suggestionData[suggestionList[index]] = {
        content: {
          inserted: null,
          deleted: null,
        },
        type: null,
        location: null,
        priority: null,
        status: null,
        comments: [],
        votes: [],
        id: suggestionList[index],
        ...dataObjs[suggestionList[index]].get([]),
      };
    }

    this.debugMessage('[SuggestionController] loadSuggestionData', suggestionList, suggestionData);

    this.Data.events?.emit(
      'SUGGESTION_LOAD',
      suggestionList,
      RichTextParser.parseSuggestions(suggestionData),
    );
  }

  private handleSuggestionListError(error: Error) {
    Logger.error(error);
  }

  private handleSuggestionChanged(data: Editor.Data.Suggestions.SuggestionData | null) {
    this.debugMessage('[SuggestionController] handleSuggestionChanged', data);
    if (data) {
      this.Data.events?.emit('UPDATE_SUGGESTION', data);
    }
  }

  private handleSuggestionCreated(data: Editor.Data.Suggestions.SuggestionData | null) {
    this.debugMessage('[SuggestionController] handleSuggestionCreated', data);
    if (data) {
      this.Data.events?.emit('CREATE_SUGGESTION', data);
    }
  }

  get userLogged() {
    return this.Data.users?.loggedUserId;
  }

  getSuggestionModelById(suggestionId: string): Editor.Data.Suggestion | undefined {
    return this.Data.models?.get('SUGGESTION', suggestionId);
  }

  isUserAuthorOfSuggestion(suggestionId: string) {
    if (this.userLogged) {
      return this.list?.data[suggestionId].isAuthor(this.userLogged);
    }
    return false;
  }

  async getTrackedActionLocation(suggestionId: string) {
    if (!this.suggestionList) {
      return [];
    }

    await this.suggestionList.awaitForReady();
    return this.suggestionList.suggestionLocation(suggestionId);
  }

  async addTrackedAction(
    type: Editor.Data.Suggestions.SuggestionDataType,
    inserted: Editor.Data.Suggestions.SuggestionContentData,
    deleted: Editor.Data.Suggestions.SuggestionContentData,
    location: string[],
    id: string,
  ) {
    const action: Editor.Data.Suggestions.SuggestionData = {
      id,
      content: {
        deleted,
        inserted,
      },
      type,
      location,
      priority: 'MEDIUM',
      status: 'OPEN',
      author: this.userLogged ?? null,
      comments: [],
      votes: [],
      time: new Date().toISOString(),
    };
    const newSuggestion = this.Data.models?.get('SUGGESTION', id);

    if (newSuggestion) {
      //WARN: needs this listener for onboarding
      newSuggestion.on('CREATED', this.handleSuggestionCreated);
      return newSuggestion.create(action);
    }
  }

  updateSuggestionType(
    suggestionId: string,
    type: Editor.Data.Suggestions.SuggestionData['type'],
    options?: Realtime.Core.RealtimeSourceOptions,
  ) {
    return this.getSuggestionById(suggestionId)?.updateSuggestionType(type, options);
  }

  updateSuggestionContent(
    suggestionId: string,
    newData: Editor.Data.Suggestions.SuggestionData['content'],
    options?: Realtime.Core.RealtimeSourceOptions,
  ) {
    return this.getSuggestionById(suggestionId)?.updateSuggestionContent(newData, options);
  }

  /**
   * @description votes a suggestion
   * @author Filipe Assunção
   * @param {String} suggestionId the id of the suggestion to vote
   * @param {boolean} vote the vote value
   * @returns {Promise<any>} Promise with the suggestion id or error
   * @memberof suggestionPersistor
   */
  voteTrackedAction(suggestionId: string, vote: boolean) {
    return this.getSuggestionById(suggestionId)?.vote(vote);
  }

  /**
   * @description persists a suggestion reply
   * @author Filipe Assunção
   * @param {String} suggestionId the id of the suggestion to reply
   * @param {String} content the reply content
   * @returns {Promise<any>} Promise with the suggestion id or error
   * @memberof suggestionPersistor
   */
  replyTrackedAction(suggestionId: string, reply: RichTextJSON) {
    return this.getSuggestionById(suggestionId)?.reply(
      {
        dir: 'ltr',
        content: reply,
        v: RichTextParser.getVersion,
      },
      this.userLogged ?? null,
    );
  }

  /**
   * @description votes a suggestion reply
   * @author Filipe Assunção
   * @param {String} suggestionId the id of the reply's suggestion
   * @param {String} replyId the id of the reply to vote
   * @param {boolean} vote the vote value
   * @returns {Promise<any>} Promise with the suggestion id or error
   */
  voteTrackedActionReply(suggestionId: string, replyId: string, vote: boolean) {
    return this.getSuggestionById(suggestionId)?.voteReply(replyId, vote);
  }

  /**
   * @description edits a suggestion reply
   * @author Filipe Assunção
   * @param {String} suggestionId the id of the reply's suggestion
   * @param {String} replyId the id of the reply to edit
   * @param {RichTextJSON} newContent the reply new content
   * @returns {Promise<any>} Promise with the suggestion id or error
   */
  editTrackedActionReply(suggestionId: string, replyId: string, newContent: RichTextJSON) {
    return this.getSuggestionById(suggestionId)?.editReply(replyId, newContent);
  }

  /**
   * @description deletes a suggestion reply
   * @author Filipe Assunção
   * @param {String} suggestionId the id of the reply's suggestion
   * @param {String} replyId the id of the reply to delete
   * @returns {Promise<any>} Promise with the suggestion id or error
   */
  deleteTrackedActionReply(suggestionId: string, replyId: string) {
    return this.getSuggestionById(suggestionId)?.deleteReply(replyId);
  }

  /**
   * @description edits a suggestion content
   * @author Filipe Assunção
   * @param {String} suggestionId the id of the suggestion to edit
   * @param {SuggestionDataPriority} priority priority of the suggestion
   * @returns {Promise<any>} Promise with the suggestion id or error
   * @memberof suggestionPersistor
   */
  changeTrackedActionPriority(
    suggestionId: string,
    priority: Editor.Data.Suggestions.SuggestionDataPriority,
  ) {
    return this.getSuggestionById(suggestionId)?.changePriority(priority);
  }

  /**
   * @description resolves a suggestion
   * @author Filipe Assunção
   * @param {String} suggestionId the id of the suggestion to resolve
   * @returns {Promise<any>} Promise with the suggestion id or error
   */
  async acceptTrackedAction(suggestionId: string) {
    await this.getSuggestionById(suggestionId)?.accept();
    this.Data.models?.undoManager?.clearUndoRedo();
  }

  /**
   * @description resolves a suggestion
   * @author Filipe Assunção
   * @param {String} suggestionId the id of the suggestion to resolve
   * @returns {Promise<any>} Promise with the suggestion id or error
   */
  async rejectTrackedAction(suggestionId: string) {
    await this.getSuggestionById(suggestionId)?.reject();
    this.Data.models?.undoManager?.clearUndoRedo();
  }

  toggleTracking(value: boolean) {
    return new Promise<void>((resolve, reject) => {
      this.Data.transport.dispatchEvent(
        'DOCUMENT:TRACKING',
        {
          value,
        },
        (response) => {
          if (response.success) {
            resolve();
          } else {
            reject(response.error);
          }
        },
      );
    });
  }

  lockTracking(state: boolean, lock: boolean) {
    return new Promise((resolve, reject) => {
      this.Data.transport.dispatchEvent(
        'LOCK:TRACKING:STATE',
        {
          state,
          lock,
        },
        (response) => {
          if (response.success) {
            resolve(response.payload?.document);
          } else {
            reject(response.error);
          }
        },
      );
    });
  }

  /**
   * @description resolves a suggestion
   * @author Filipe Assunção
   * @param {String} suggestionId the id of the suggestion to resolve
   * @returns {Promise<any>} Promise with the suggestion id or error
   * @memberof suggestionPersistor
   */
  acceptSuggestions(suggestionIds: string[] | string) {
    this.Data.models?.undoManager?.clearUndoRedo();
    return new Promise<void>((resolve, reject) => {
      this.Data.transport.dispatchEvent(
        'TRACKED:ACTION:ACCEPT',
        {
          trackedActionId: suggestionIds,
        },
        (response) => {
          if (response.success) {
            resolve();
          } else {
            reject(response.error);
          }
        },
      );
    });
  }

  /**
   * @description resolves a suggestion
   * @author Filipe Assunção
   * @param {String} suggestionId the id of the suggestion to resolve
   * @returns {Promise<any>} Promise with the suggestion id or error
   * @memberof suggestionPersistor
   */
  rejectSuggestions(suggestionIds: string[] | string) {
    this.Data.models?.undoManager?.clearUndoRedo();
    return new Promise<void>((resolve, reject) => {
      this.Data.transport.dispatchEvent(
        'TRACKED:ACTION:REJECT',
        {
          trackedActionId: suggestionIds,
        },
        (response) => {
          if (response.success) {
            resolve();
          } else {
            reject(response.error);
          }
        },
      );
    });
  }

  /**
   * @description Accepts all of the current user pending tracked actions
   * @author Filipe Assunção
   * @returns {Promise<any>} Promise with the suggestion id or error
   * @memberof suggestionPersistor
   */
  acceptAllTrackedActions(trackedActionsRefs: string[]) {
    this.Data.models?.undoManager?.clearUndoRedo();
    return new Promise<void>((resolve, reject) => {
      this.Data.transport.dispatchEvent(
        'TRACKED:ACTION:ALL:ACCEPT',
        { trackedActionsRefs: trackedActionsRefs },
        (response) => {
          if (response.success) {
            resolve();
          } else {
            reject();
          }
        },
      );
    });
  }

  /**
   * @description Rejects all of the current user pending tracked actions
   * @author Filipe Assunção
   * @returns {Promise<any>} Promise with the suggestion id or error
   * @memberof suggestionPersistor
   */
  rejectAllTrackedActions(trackedActionsRefs: string[]) {
    this.Data.models?.undoManager?.clearUndoRedo();
    return new Promise<void>((resolve, reject) => {
      this.Data.transport.dispatchEvent(
        'TRACKED:ACTION:ALL:REJECT',
        { trackedActionsRefs: trackedActionsRefs },
        (response) => {
          if (response.success) {
            resolve();
          } else {
            reject();
          }
        },
      );
    });
  }

  stop(): void { }

  destroy(): void {
    this.suggestionList?.destroy();

    const timeoutKeys = Object.typedKeys(this.timeouts);
    for (let i = 0; i < timeoutKeys.length; i++) {
      if (this.timeouts[timeoutKeys[i]]) {
        clearTimeout(this.timeouts[timeoutKeys[i]]);
        delete this.timeouts[timeoutKeys[i]];
      }
    }
  }

  async applyOpsToSuggestion(
    suggestionId: string,
    ops: Realtime.Core.RealtimeOps,
    options?: Realtime.Core.RealtimeSourceOptions,
  ) {
    const model = this.getSuggestionModelById(suggestionId);
    if (model?.loaded) {
      return model.apply(ops, options);
    }
  }

  async revertOpsToSuggestion(
    suggestionId: string,
    ops: Realtime.Core.RealtimeOps,
    options?: Realtime.Core.RealtimeSourceOptions,
  ) {
    const model = this.getSuggestionModelById(suggestionId);
    if (model?.loaded) {
      return model.revert(ops, options);
    }
  }

  // #######################################################3
  // Suggestions content update
  addSuggestionRefToUpdate(ref: string) {
    this.logMessage('INFO', `addSuggestionRefToUpdate ${ref}`);

    if (!this.isSuggestionCreated(ref)) {
      // add suggestion to be created after reindex
      if (!this.suggestionRefsToCreate.includes(ref)) {
        this.suggestionRefsToCreate.push(ref);
      }

      this.scheduleCreateSuggestionData(1000);
    } else if (!this.suggestionRefsToCreate.includes(ref)) {
      // add sugestion to be updated
      if (!this.suggestionRefsToUpdate.includes(ref)) {
        this.suggestionRefsToUpdate.push(ref);
      }

      this.scheduleUpdateSuggestionData();
    }
  }

  protected getSuggestionContent(
    suggestionData: Editor.Data.Node.Data[],
  ): Editor.Data.Suggestions.SuggestionContentData | undefined {
    let result: Editor.Data.Suggestions.SuggestionContentData | undefined = undefined;

    for (let i = 0; i < suggestionData.length; i++) {
      let type: Editor.Data.Suggestions.SuggestionContentData['type'] = null;
      let content: string = '';

      if (NodeUtils.isParagraphMarker(suggestionData[i])) {
        content = '\n';
        type = 'text';
      } else {
        const queryResult = NodeUtils.querySelectorInData(
          suggestionData[i],
          [
            'text',
            'tbl',
            'figure',
            ...NodeUtils.INLINE_NON_EDITABLE_TYPES,
            ...NodeUtils.BLOCK_NON_EDITABLE_TYPES,
            // ...NodeUtils.INLINE_WRAP_TYPES,
            // ELEMENTS.HyperlinkElement.ELEMENT_TYPE
          ],
          undefined,
          { onlyFirstChild: true },
        );

        for (let i = 0; i < queryResult.length; i++) {
          const element = queryResult[i];
          switch (element.data.type) {
            case 'figure':
            case 'image-element':
            case 'img':
              if (type !== 'text') {
                type = 'figure';
              }
              break;
            case 'pb':
              type = 'text';
              content += 'Page Break';
              break;
            case 'sb':
              type = 'text';
              content += 'Section Break';
              break;
            case 'cb':
              type = 'text';
              content += 'Column Break';
              break;
            case 'text':
              type = 'text';
              content += element.data.content;
              break;
            default:
              if (type !== 'text') {
                type = element.data.type;
              }
              break;
          }
        }
      }

      if (type != null) {
        if (result) {
          if (result.type === 'text' && type === 'text') {
            result.value += content;
          } else if (type === 'text') {
            result.type = type;
            result.value = content;
          } else if (result.type !== 'text' && result.type !== type) {
            result.type = null;
            result.value = null;
          }
        } else {
          result = {
            type: type,
            value: content,
          };
        }
      }
    }

    return result;
  }

  private async getSuggestionData(reference: string): Promise<SuggestionData | undefined> {
    let suggestionData: SuggestionData | undefined = undefined;

    let blockIds: string[] = [];

    // get block ids from other suggestion locations
    const suggestionLocations = await this.getTrackedActionLocation(reference);

    for (let s = 0; s < suggestionLocations.length; s++) {
      if (!blockIds.includes(suggestionLocations[s].level0)) {
        blockIds.push(suggestionLocations[s].level0);
      }
    }

    // sort block ids
    blockIds = blockIds.sort((a, b) => {
      const aIndex = this.Data.structure?.getIndexOfNode(a) || 0;
      const bIndex = this.Data.structure?.getIndexOfNode(b) || 0;
      return aIndex - bIndex;
    });

    if (blockIds.length) {
      const trackedInsert: Editor.Data.Node.Data[] = [];
      const trackedDelete: Editor.Data.Node.Data[] = [];

      // filter inserted and deleted suggestions
      for (let b = 0; b < blockIds.length; b++) {
        const data = this.Data.nodes?.getNodeModelById(blockIds[b])?.selectedData();

        if (data) {
          const result = NodeUtils.querySelectorInData(data, ['tracked-insert', 'tracked-delete'], {
            element_reference: reference,
          });

          for (let i = 0; i < result.length; i++) {
            if (result[i].data.type === 'tracked-insert') {
              trackedInsert.push(result[i].data);
            }

            if (result[i].data.type === 'tracked-delete') {
              trackedDelete.push(result[i].data);
            }
          }
        }
      }

      let insertedContent = this.getSuggestionContent(trackedInsert);
      let deletedContent = this.getSuggestionContent(trackedDelete);

      let type: Editor.Edition.SuggestionData['type'] = 'INSERT';

      if (insertedContent && deletedContent) {
        type = 'REPLACE';
      } else if (insertedContent) {
        type = 'INSERT';
      } else if (deletedContent) {
        type = 'DELETE';
      }

      suggestionData = {
        type,
        insertedContent,
        deletedContent,
      };
    }

    return suggestionData;
  }

  private scheduleCreateSuggestionData(time: number = 500) {
    this.debugMessage(
      '[SuggestionController] scheduleCreateSuggestionData',
      this.suggestionRefsToCreate,
    );
    if (this.timeouts.createSuggestionData) {
      clearTimeout(this.timeouts.createSuggestionData);
    }

    this.timeouts.createSuggestionData = setTimeout(() => {
      this.createSuggestionData();
    }, time);
  }

  private async createSuggestionData() {
    this.debugMessage('[SuggestionController] createSuggestionData', this.suggestionRefsToCreate);

    if (!this.Data.structure || !this.Data.nodes) {
      return;
    }

    const missingSuggestionRefs: string[] = [];
    let createPatch: boolean = false;

    for (let i = 0; i < this.suggestionRefsToCreate.length; i++) {
      const currentRef = this.suggestionRefsToCreate[i];

      if (this.suggestionList?.list.includes(currentRef) === false) {
        missingSuggestionRefs.push(currentRef);
        continue;
      }

      const suggestionData = await this.getSuggestionData(currentRef);

      if (suggestionData) {
        // create suggestion
        this.addTrackedAction(
          suggestionData.type,
          suggestionData.insertedContent || { type: null, value: null },
          suggestionData.deletedContent || { type: null, value: null },
          [],
          currentRef,
        );

        createPatch = true;
      }
    }

    if (createPatch) {
      // TODO: think of a better solution to save suggestion changes in the same patch as the node changes
      this.Data.history?.createPatch();
    }

    // handle missing suggestion refs
    this.suggestionRefsToCreate = missingSuggestionRefs;

    if (this.suggestionRefsToCreate.length) {
      if (this.rescheduleCreateSuggestionDataCounter < 5) {
        this.rescheduleCreateSuggestionDataCounter++;
        this.scheduleCreateSuggestionData(1000 * this.rescheduleCreateSuggestionDataCounter);
      } else {
        this.rescheduleCreateSuggestionDataCounter = 0;
      }
    } else {
      this.rescheduleCreateSuggestionDataCounter = 0;
    }
  }

  private scheduleUpdateSuggestionData(time: number = 500) {
    this.debugMessage(
      '[SuggestionController] scheduleUpdateSuggestionData',
      this.suggestionRefsToUpdate,
    );
    if (this.timeouts.updateSuggestionData) {
      clearTimeout(this.timeouts.updateSuggestionData);
    }

    this.timeouts.updateSuggestionData = setTimeout(() => {
      this.updateSuggestionData();
    }, time);
  }

  private async updateSuggestionData() {
    this.debugMessage('[SuggestionController] updateSuggestionData', this.suggestionRefsToUpdate);

    if (!this.Data.structure || !this.Data.nodes) {
      return;
    }

    // removes refs from original array and creates a new array to work with
    const refs = this.suggestionRefsToUpdate.splice(0, this.suggestionRefsToUpdate.length);

    for (let r = 0; r < refs.length; r++) {
      const suggestionRef = refs[r];

      const suggestionData = await this.getSuggestionData(suggestionRef);

      if (suggestionData) {
        // update suggestion
        this.updateSuggestionType(suggestionRef, suggestionData.type);
        this.updateSuggestionContent(suggestionRef, {
          inserted: suggestionData.insertedContent || { type: null, value: null },
          deleted: suggestionData.deletedContent || { type: null, value: null },
        });
      }

      // TODO: think of a better solution to save suggestion changes in the same patch as the node changes
      this.Data.history?.createPatch();
    }
  }
}
